// src/components/DocumentManagement.js

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import 'animate.css';
import { usePermissions } from '../App'; // Import the permissions hook
import DocumentExplorer from './DocumentExplorer';

import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, Modifier } from 'draft-js';
import 'draft-js/dist/Draft.css'; // Import Draft.js's default CSS
import { parseISO, format } from 'date-fns';
import draftToHtml from 'draftjs-to-html';

// --- NEW: Helper function to get client display name ---
const getClientDisplayName = (client) => {
  if (!client) {
    return 'N/A';
  }
  if (client.client_type === 'individual' && client.first_name) {
    return `${client.first_name} ${client.last_name || ''}`.trim();
  }
  if (client.client_type === 'organizational' && client.organization_name) {
    return client.organization_name;
  }
  return 'N/A';
};

// --- NEW: Draft.js Toolbar Component ---
const StyleButton = ({ onToggle, style, active, label }) => {
  const onMouseDown = (e) => {
    e.preventDefault();
    onToggle(style);
  };
  let className = 'RichEditor-styleButton p-2 rounded-md transition-colors duration-200 cursor-pointer';
  if (active) {
    className += ' bg-gray-300 text-gray-800';
  } else {
    className += ' hover:bg-gray-200 text-gray-600';
  }
  return (
    <span className={className} onMouseDown={onMouseDown} title={typeof label === 'string' ? label : ''}>
      {label}
    </span>
  );
};

const BLOCK_TYPES = [
    { label: 'H1', style: 'header-one', icon: 'bi-type-h1' },
    { label: 'H2', style: 'header-two', icon: 'bi-type-h2' },
    { label: 'H3', style: 'header-three', icon: 'bi-type-h3' },
    { label: 'UL', style: 'unordered-list-item', icon: 'bi-list-ul' },
    { label: 'OL', style: 'ordered-list-item', icon: 'bi-list-ol' },
    { label: 'Quote', style: 'blockquote', icon: 'bi-blockquote-left' },
    { label: 'Code', style: 'code-block', icon: 'bi-code' },
];

const INLINE_STYLES = [
    { label: 'Bold', style: 'BOLD', icon: 'bi-type-bold' },
    { label: 'Italic', style: 'ITALIC', icon: 'bi-type-italic' },
    { label: 'Underline', style: 'UNDERLINE', icon: 'bi-type-underline' },
    { label: 'Monospace', style: 'CODE', icon: 'bi-code' }
];

// New helper component for block type dropdowns
const BlockStyleControls = ({ editorState, onToggleBlockType }) => {
    const selection = editorState.getSelection();
    const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();
    
    return (
        <div className="RichEditor-controls flex items-center gap-2">
            {BLOCK_TYPES.map(type => (
                <StyleButton
                    key={type.label}
                    active={type.style === blockType}
                    label={<i className={`bi ${type.icon}`}></i>}
                    onToggle={onToggleBlockType}
                    style={type.style}
                />
            ))}
        </div>
    );
};

// New helper component for inline style controls
const InlineStyleControls = ({ editorState, onToggleInlineStyle }) => {
    const currentStyle = editorState.getCurrentInlineStyle();
    return (
        <div className="RichEditor-controls flex items-center gap-2">
            {INLINE_STYLES.map(type => (
                <StyleButton
                    key={type.label}
                    active={currentStyle.has(type.style)}
                    label={<i className={`bi ${type.icon}`}></i>}
                    onToggle={onToggleInlineStyle}
                    style={type.style}
                />
            ))}
        </div>
    );
};

const RichTextEditorToolbar = ({ editorState, onToggleBlockType, onToggleInlineStyle, setTemplateContent }) => {
    // Handlers for the new functionalities
    const handleInsertLink = (e) => {
        e.preventDefault();
        const selection = editorState.getSelection();
        if (selection.isCollapsed()) {
            // Replaced alert with a more user-friendly mechanism if available, e.g., a toast notification
            console.warn('Please select some text to make it a link.');
            return;
        }
        const url = window.prompt('Enter the URL for the link:');
        if (url && url.trim() !== '') {
            const contentState = editorState.getCurrentContent();
            const contentWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url });
            const entityKey = contentWithEntity.getLastCreatedEntityKey();
            const newContentState = Modifier.applyEntity(contentWithEntity, selection, entityKey);
            const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
            setTemplateContent(newEditorState);
        }
    };

    const handleInsertLogo = (e) => {
      e.preventDefault();
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const newContentState = Modifier.insertText(
        contentState,
        selection,
        '[LOGO]'
      );
      setTemplateContent(EditorState.push(editorState, newContentState, 'insert-characters'));
    };

    const handleInsertHorizontalLine = (e) => {
      e.preventDefault();
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const newContentState = Modifier.insertText(
        contentState,
        selection,
        '<hr class="w-full border-t border-gray-300 my-4" />'
      );
      setTemplateContent(EditorState.push(editorState, newContentState, 'insert-characters'));
    };

    return (
      <div className="RichEditor-controls flex flex-wrap gap-2 mb-4 p-2 bg-gray-100 rounded-md shadow-inner">
        <InlineStyleControls
          editorState={editorState}
          onToggleInlineStyle={onToggleInlineStyle}
        />
        <BlockStyleControls
          editorState={editorState}
          onToggleBlockType={onToggleBlockType}
        />
        <div className="w-px bg-gray-300 mx-2 self-stretch"></div>
        {/* NEW: Insert Link and Logo buttons */}
        <span
          className="RichEditor-styleButton p-2 rounded-md transition-colors duration-200 cursor-pointer hover:bg-gray-200 text-gray-800"
          onMouseDown={handleInsertLink}
          title="Insert Link"
        >
          <i className="bi bi-link-45deg"></i>
        </span>
        <span
          className="RichEditor-styleButton p-2 rounded-md transition-colors duration-200 cursor-pointer hover:bg-gray-200 text-gray-800"
          onMouseDown={handleInsertLogo}
          title="Insert Logo"
        >
          <i className="bi bi-image"></i>
        </span>
        <span
          className="RichEditor-styleButton p-2 rounded-md transition-colors duration-200 cursor-pointer hover:bg-gray-200 text-gray-800"
          onMouseDown={handleInsertHorizontalLine}
          title="Insert Horizontal Line"
        >
          <i className="bi bi-layout-text-window-reverse"></i>
        </span>
      </div>
    );
};

// --- NEW: DocumentManagement Component --
function DocumentManagement({
  documents,
  documentTemplates,
  documentVersions,
  setDocumentVersions,
  cases,
  clients,
  authAxios,
  fetchDocuments,
  fetchDocumentVersions,
  fetchDocumentTemplates,
  currentUser,
  documentTypeOptions,
  handleDelete,
  showFormMessage,
  navigateTo,
  setNavigateTo,
  onOpenEditor
}) {
  const permissions = usePermissions(); // Use the permissions hook
  // Local toast UI removed; rely on global showFormMessage when available

  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [showDocumentVersionsModal, setShowDocumentVersionsModal] = useState(false);
  const [showDocumentDetailsModal, setShowDocumentDetailsModal] = useState(false);
  const [showGenerateDocumentModal, setShowGenerateDocumentModal] = useState(false);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [templateContent, setTemplateContent] = useState(() => EditorState.createEmpty());
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [documentsViewMode, setDocumentsViewMode] = useState('tiles');
  const [templatesViewMode, setTemplatesViewMode] = useState('tiles');
  const [showExplorer, setShowExplorer] = useState(true);
  const [explorerCaseId, setExplorerCaseId] = useState('');
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    description: '',
    template_content: '',
    document_type: 'Other',
    is_public: false,
    // logo field removed - now using firm's logo
  });
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [generatingDocument, setGeneratingDocument] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: '',
    document_type: 'Other', 
    description: '',
    client: '',
    case: '',
    file: null, 
  });
  const [editDocument, setEditDocument] = useState(null);
  const [selectedDocumentForVersions, setSelectedDocumentForVersions] = useState(null);
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [newVersionNotes, setNewVersionNotes] = useState('');
  const [selectedDocumentDetails, setSelectedDocumentDetails] = useState(null);
  const [generationClientId, setGenerationClientId] = useState('');
  const [generationCaseId, setGenerationCaseId] = useState('');
  const [generationDebtRecoveryRequestId, setGenerationDebtRecoveryRequestId] = useState('');
  const [generationDebtRecoveryRequests, setGenerationDebtRecoveryRequests] = useState([]);
  const [generationDebtRecoveryLoading, setGenerationDebtRecoveryLoading] = useState(false);
  const [generationDebtRecoveryError, setGenerationDebtRecoveryError] = useState('');
  // Drafting wizard extended state
  const [wizardStep, setWizardStep] = useState(1); // 1: Select, 2: Variables, 3: Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [overrideToday, setOverrideToday] = useState('');
  const [detectedPlaceholders, setDetectedPlaceholders] = useState([]);
  const [placeholderOverrides, setPlaceholderOverrides] = useState({});
  
  const [filteredCases, setFilteredCases] = useState([]);
  const [generationFilteredCases, setGenerationFilteredCases] = useState([]);
  const [generationSelectedService, setGenerationSelectedService] = useState('');
  const [generationTemplateServices, setGenerationTemplateServices] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');

  const editorRef = useRef(null);

  const dynamicPlaceholders = [
    { label: "Client's First Name", value: "{{ client.first_name }}" },
    { label: "Client's Last Name", value: "{{ client.last_name }}" },
    { label: "Client's Organization Name", value: "{{ client.organization_name }}" },
    { label: "Client's Address", value: "{{ client.physical_residential_address }}"},
    { label: "Client's ID/Passport Number", value: "{{ client.id_or_passport_number }}"},
    { label: "Client's Email", value: "{{ client.email }}" },
    { label: "Client's Phone", value: "{{ client.phone_number }}" },
    { label: "Client's Organization Address", value: "{{ client.physical_business_address }}"},
    { label: "Client's Organization Contact Person", value: "{{ client.contact_person }}" },
    { label: "Client's Organization Registration Number", value: "{{ client.registration_number }}"},
    { label: "Client's Organization Date of Incorporation", value: "{{ client.date_of_incorporation }}" },
    { label: "Client's Organization Tax ID", value: "{{ client.tax_identification_number }}" },
    { label: "Case Attorney", value: "{{ case.partner.first_name }}"},
    { label: "Case Attorney Last Name", value: "{{ case.partner.last_name }}"},
    { label: "Case Attorney Title", value: "{{ case.attorney_title }}"},
    { label: "Case Description", value: "{{ case.description }}"},
    { label: "List of Services Provided By Firm", value: "{{ firm.services_list }}"},
    { label: "Service (selected)", value: "{{ services.selected }}"},
    { label: "Attorney Phone", value: "{{ case.partner.phone_number }}"},
    { label: "Attorney Email", value: "{{ case.partner.email }}"},
    { label: "Paralegals / Associates", value: "{{ case.paralegals_list }}"},
    { label: "Retainer Amount", value: "{{ case.retainer_amount }}"},
    { label: "Client Trust Account Name", value: "{{ client.trust_account_name }}"},
    { label: "Case Number", value: "{{ case.case_number }}" },
    { label: "Case Title", value: "{{ case.title }}" },
    { label: "Case Number", value: "{{ case.case_number }}" },
    { label: "Date", value: "{{ today }}" },
    { label: "Debtor Name", value: "{{ debtor_name }}" },
    { label: "DRR Principal Amount", value: "{{ principal_amount }}" },
    { label: "DRR Outstanding Amount", value: "{{ outstanding_amount }}" },
    { label: "DRR Total Recovered", value: "{{ total_recovered }}" },
    { label: "DRR Contingency %", value: "{{ contingency_percent }}" },
    { label: "DRR Fixed Fee", value: "{{ fixed_fee_amount }}" },
    { label: "DRR Success Fee Due", value: "{{ success_fee_due }}" },
  ];

  // Helper to format numeric price values into currency-like strings
  const formatPrice = (val) => {
    if (val === undefined || val === null || val === '') return '';
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const DOCUMENT_TYPE_CHOICES = [
    { label: 'Client Document', value: 'Client' },
    { label: 'Pleadings', value: 'Pleadings' },
    { label: 'Contract', value: 'Contract' },
    { label: 'Correspondence', value: 'Correspondence' },
    { label: 'Evidence', value: 'Evidence' },
    { label: 'Research', value: 'Research' },
    { label: 'Internal Memo/Draft', value: 'Internal Memo/Draft' },
    { label: 'Agreement', value: 'Agreement' },
    { label: 'Other', value: 'Other' }
  ];
  
  const showToast = useCallback((message, type = 'success') => {
    if (typeof showFormMessage === 'function') {
      showFormMessage(type, message);
      return;
    }
    console.warn('Toast:', type, message);
  }, [showFormMessage]);

  // Define modal closing functions first and wrap in useCallback
  const closeAddDocumentModal = useCallback(() => setShowAddDocumentModal(false), []);
  const closeEditDocumentModal = useCallback(() => setShowEditDocumentModal(false), []);
  const closeDocumentDetailsModal = useCallback(() => setShowDocumentDetailsModal(false), []);
  const closeGenerateDocumentModal = useCallback(() => setShowGenerateDocumentModal(false), []);
  const closeAddTemplateModal = useCallback(() => {
    setShowAddTemplateModal(false);
    setEditingTemplate(null);
  }, []);
   const closeDocumentVersionsModal = useCallback(() => {
    setSelectedDocumentForVersions(null);
    setShowDocumentVersionsModal(false);
    setNewVersionFile(null);
    setNewVersionNotes('');
    setDocumentVersions([]);
  }, [setDocumentVersions]);

  useEffect(() => {
    // Inject Bootstrap Icons CSS into the document head
    const iconLink = document.createElement('link');
    iconLink.rel = 'stylesheet';
    iconLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css';
    document.head.appendChild(iconLink);
    
    fetchDocuments();
    fetchDocumentTemplates();

    return () => {
      // Cleanup the link when the component unmounts
      document.head.removeChild(iconLink);
    };
  }, [fetchDocuments, fetchDocumentTemplates]);
  
  useEffect(() => {
    if (newDocument.client) {
      const selectedClientId = parseInt(newDocument.client, 10);
      setFilteredCases(cases.filter(c => c.client === selectedClientId));
    } else {
      setFilteredCases(cases);
    }
  }, [newDocument.client, cases]);

  useEffect(() => {
    if (generationClientId) {
      const selectedClientId = parseInt(generationClientId, 10);
      setGenerationFilteredCases(cases.filter(c => c.client === selectedClientId));
    } else {
      setGenerationFilteredCases([]);
    }
  }, [generationClientId, cases]);

  useEffect(() => {
    let mounted = true;
    const fetchDebtRecoveryRequests = async () => {
      if (!generationClientId || !authAxios) {
        if (mounted) {
          setGenerationDebtRecoveryRequests([]);
          setGenerationDebtRecoveryError('');
        }
        return;
      }
      setGenerationDebtRecoveryLoading(true);
      setGenerationDebtRecoveryError('');
      try {
        const resp = await authAxios.get(`debt-recovery-requests/?requesting_client=${generationClientId}&page_size=200`);
        const data = resp?.data;
        const rows = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        if (mounted) setGenerationDebtRecoveryRequests(rows);
      } catch (e) {
        console.error('Failed to load debt recovery requests for client', e);
        if (mounted) {
          setGenerationDebtRecoveryRequests([]);
          setGenerationDebtRecoveryError('Failed to load debt recovery requests.');
        }
      } finally {
        if (mounted) setGenerationDebtRecoveryLoading(false);
      }
    };
    fetchDebtRecoveryRequests();
    return () => { mounted = false; };
  }, [authAxios, generationClientId]);

  const generationFilteredDebtRecoveryRequests = useMemo(() => {
    if (!generationCaseId) return generationDebtRecoveryRequests;
    return generationDebtRecoveryRequests.filter((r) => String(r.case || r.case_id || '') === String(generationCaseId));
  }, [generationDebtRecoveryRequests, generationCaseId]);

  useEffect(() => {
    if (!generationDebtRecoveryRequestId) return;
    const exists = generationFilteredDebtRecoveryRequests.some((r) => String(r.id) === String(generationDebtRecoveryRequestId));
    if (!exists) {
      setGenerationDebtRecoveryRequestId('');
    }
  }, [generationDebtRecoveryRequestId, generationFilteredDebtRecoveryRequests]);
  
    const focusEditor = () => {
        if (editorRef.current) {
          editorRef.current.focus();
        }
    };

  const openDocumentDetailsModal = useCallback((document) => {
    // Enrich document details with client object when available so the details modal can show client name
    let enriched = document;
    try {
      const clientId = document && (document.client || document.client_id || document.client_id === 0 ? (document.client || document.client_id) : null);
      if (clientId && Array.isArray(clients)) {
        const foundClient = clients.find(c => String(c.id) === String(clientId));
        if (foundClient) {
          enriched = { ...document, client_details: foundClient };
        }
      }
    } catch (e) {
      // ignore enrichment errors
    }
    setSelectedDocumentDetails(enriched);
    setShowDocumentDetailsModal(true);
  }, [clients]);

  // Listen for external request to set explorer case (from CaseDetailsPage "Open in Documents")
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      if (detail.caseId) {
        setExplorerCaseId(String(detail.caseId));
        setShowExplorer(true);
      }
    };
    window.addEventListener('set-explorer-case', handler);
    return () => window.removeEventListener('set-explorer-case', handler);
  }, []);
  // Auto-open document detail via navigateTo
  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'documents') {
      const doc = documents.find(d => d.id === navigateTo.id);
      if (doc) {
        openDocumentDetailsModal(doc);
        setNavigateTo && setNavigateTo(null);
      }
    }
  }, [navigateTo, documents, openDocumentDetailsModal, setNavigateTo]);

  useEffect(() => {
    if (navigateTo && navigateTo.tab === 'documents' && navigateTo.type === 'drr_document') {
      const clientId = navigateTo.clientId ? String(navigateTo.clientId) : '';
      const caseId = navigateTo.caseId ? String(navigateTo.caseId) : '';
      const drrId = navigateTo.debtRecoveryRequestId ? String(navigateTo.debtRecoveryRequestId) : '';

      if (navigateTo.mode === 'template') {
        openGenerateDocumentModal(navigateTo.templateId || '');
        setGenerationClientId(clientId);
        setGenerationCaseId(caseId);
        setGenerationDebtRecoveryRequestId(drrId);
      } else if (navigateTo.mode === 'draft') {
        openAddTemplateModal();
      } else {
        openAddDocumentModal();
        setNewDocument(prev => ({
          ...prev,
          client: clientId,
          case: caseId,
          document_type: navigateTo.mode === 'upload' ? 'Correspondence' : prev.document_type,
          description: navigateTo.mode === 'upload' ? 'Proof of delivery / correspondence' : prev.description,
        }));
      }

      setNavigateTo && setNavigateTo(null);
    }
  }, [navigateTo, documents]);

  // Auto-open via custom event (open-resource-detail)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'documents') return;
      const doc = documents.find(d => d.id === detail.id);
      if (doc) {
        openDocumentDetailsModal(doc);
        setNavigateTo && setNavigateTo(null);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [documents, openDocumentDetailsModal, setNavigateTo]);

  // Central handler for all editor state changes.
  const onEditorStateChange = useCallback((newEditorState) => {
    setTemplateContent(newEditorState);
    const contentState = newEditorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);
    setNewTemplate(prev => ({
      ...prev,
      template_content: JSON.stringify(rawContent),
    }));
  }, []);

  const onToggleBlockType = useCallback((blockType) => {
    onEditorStateChange(RichUtils.toggleBlockType(templateContent, blockType));
  }, [templateContent, onEditorStateChange]);

  const onToggleInlineStyle = useCallback((inlineStyle) => {
    onEditorStateChange(RichUtils.toggleInlineStyle(templateContent, inlineStyle));
  }, [templateContent, onEditorStateChange]);

  const handleKeyCommand = useCallback((command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      onEditorStateChange(newState);
      return 'handled';
    }
    return 'not-handled';
  }, [onEditorStateChange]);

  const handleTemplateSubmit = useCallback(async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', newTemplate.title);
    formData.append('description', newTemplate.description);
    if (typeof newTemplate.template_content === 'object') {
        formData.append('template_content', JSON.stringify(newTemplate.template_content));
    } else {
        formData.append('template_content', newTemplate.template_content);
    }
    formData.append('document_type', newTemplate.document_type);
    formData.append('is_public', newTemplate.is_public);
    // Logo field removed - now using firm's logo for consistency
    
    const url = editingTemplate ? `/document-templates/${editingTemplate.id}/` : '/document-templates/';
    const method = editingTemplate ? 'patch' : 'post';

    try {
      await authAxios[method](url, formData);
      showToast(`Template ${editingTemplate ? 'updated' : 'added'} successfully!`, 'success');
      fetchDocumentTemplates();
      closeAddTemplateModal();
    } catch (error) {
      console.error(`Error ${editingTemplate ? 'updating' : 'adding'} template:`, error);
      showToast(`Error ${editingTemplate ? 'updating' : 'adding'} template.`, 'danger');
    }
  }, [authAxios, newTemplate, editingTemplate, fetchDocumentTemplates, closeAddTemplateModal, showToast]);


  const getBlockStyle = (block) => {
    const blockType = block.getType();
    let className = 'DraftEditor-block'; // Base class for targeting
  
    switch (blockType) {
      case 'header-one':
        className += ' text-3xl font-bold my-4';
        break;
      case 'header-two':
        className += ' text-2xl font-bold my-3';
        break;
      case 'header-three':
        className += ' text-xl font-bold my-2';
        break;
      case 'blockquote':
        className += ' border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4';
        break;
      case 'unordered-list-item':
        className += ' list-item list-disc ml-8';
        break;
      case 'ordered-list-item':
        className += ' list-item list-decimal ml-8';
        break;
      case 'code-block':
        className += ' bg-gray-100 p-2 font-mono text-sm rounded my-4';
        break;
      default:
        break;
    }
    return className;
  };

  const handlePreviewTemplate = (template) => {
    const rawContentState = (typeof template.template_content === 'string')
        ? JSON.parse(template.template_content)
        : template.template_content;
    let htmlContent = draftToHtml(rawContentState);
    
    if (template.logo_url) {
      htmlContent = htmlContent.replace(
        '[LOGO]',
        `<img src="${template.logo_url}" alt="Template Logo" style="max-width: 200px; max-height: 200px; margin-bottom: 20px;" />`
      );
    } else {
      htmlContent = htmlContent.replace('[LOGO]', '');
    }
    
    setPreviewContent(htmlContent);
    setShowPreviewModal(true);
  };
  
  const handleNewTemplatePreview = () => {
    const rawContentState = convertToRaw(templateContent.getCurrentContent());
    let htmlContent = draftToHtml(rawContentState);

    // Use firm's logo from editingTemplate if available
    if (editingTemplate && editingTemplate.logo_url) {
        htmlContent = htmlContent.replace('[LOGO]', `<img src="${editingTemplate.logo_url}" alt="Firm Logo" style="max-width: 200px; max-height: 200px; margin-bottom: 20px;" />`);
    } else {
        htmlContent = htmlContent.replace('[LOGO]', '<p style="color: #999; font-style: italic;">[Firm logo will appear here]</p>');
    }

    setPreviewContent(htmlContent);
    setShowPreviewModal(true);
  };


  const handleEditTemplate = (template) => {
      setEditingTemplate(template);
      setNewTemplate({
        title: template.title,
        description: template.description,
        document_type: template.document_type,
        is_public: template.is_public,
        // logo field removed - now using firm's logo
        template_content: template.template_content,
      });
      
      try {
        const rawContentState = (typeof template.template_content === 'string')
          ? JSON.parse(template.template_content)
          : template.template_content;
        
        const contentState = convertFromRaw(rawContentState);
        setTemplateContent(EditorState.createWithContent(contentState));
      } catch(e) {
        console.error("Error parsing template content:", e);
        setTemplateContent(EditorState.createEmpty());
      }
      setShowAddTemplateModal(true);
  };

  const handleDocumentSubmit = useCallback(async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', newDocument.title);
    formData.append('document_type', newDocument.document_type);
    formData.append('description', newDocument.description);
    if (newDocument.client) formData.append('client', newDocument.client);
    if (newDocument.case) formData.append('case', newDocument.case);
    if (newDocument.file) formData.append('file', newDocument.file);

    try {
      await authAxios.post('/documents/', formData);
      showToast('Document added successfully!', 'success');
      fetchDocuments();
      closeAddDocumentModal();
    } catch (error) {
      console.error('Error adding document:', error);
      showToast('Error adding document.', 'danger');
    }
  }, [authAxios, newDocument, fetchDocuments, closeAddDocumentModal, showToast]);

  const handleEditDocumentSubmit = useCallback(async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', editDocument.title);
    formData.append('document_type', editDocument.document_type);
    formData.append('description', editDocument.description);
    if (editDocument.client) formData.append('client', editDocument.client);
    if (editDocument.case) formData.append('case', editDocument.case);

    try {
      await authAxios.patch(`/documents/${editDocument.id}/`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      showToast('Document updated successfully!', 'success');
      fetchDocuments();
      closeEditDocumentModal();
    } catch (error) {
      console.error('Error updating document:', error);
      showToast('Error updating document.', 'danger');
    }
  }, [authAxios, editDocument, fetchDocuments, closeEditDocumentModal, showToast]);

  const openAddDocumentModal = () => {
    setNewDocument({
      title: '',
      document_type: 'Other',
      description: '',
      client: '',
      case: '',      
      file: null,
    });
    setShowAddDocumentModal(true);
  };

  const openAddTemplateModal = () => {
    setEditingTemplate(null);
    setNewTemplate({
      title: '',
      description: '',
      template_content: '',
      document_type: 'Other',
      is_public: false,
      // logo field removed - now using firm's logo
    });
    setTemplateContent(EditorState.createEmpty());
    setShowAddTemplateModal(true);
  };

  const openEditDocumentModal = useCallback((doc) => {
    setEditDocument({
        id: doc.id,
        title: doc.title,
        description: doc.description,
        document_type: doc.document_type,
        client: doc.client, 
        case: doc.case,
    });
    setShowEditDocumentModal(true);
  }, []);

  const openDocumentVersionsModal = useCallback(async (document) => {
    setSelectedDocumentForVersions(document);
    setShowDocumentVersionsModal(true);
    try {
      const response = await authAxios.get(`documents/${document.id}/versions/`);
      setDocumentVersions(response.data);
    } catch (error) {
      console.error("Error fetching document versions for selected document:", error);
      showToast('Failed to load document versions.', 'danger');
      setDocumentVersions([]);
    }
  }, [authAxios, setDocumentVersions, showToast]);

  const handleNewVersionFileChange = useCallback((e) => {
    setNewVersionFile(e.target.files[0]);
  }, []);

  const handleNewVersionNotesChange = useCallback((e) => {
    setNewVersionNotes(e.target.value);
  }, []);

  const handleUploadNewVersion = useCallback(async (e) => {
    e.preventDefault();

    if (!newVersionFile) {
      showToast('Please select a file for the new version.', 'danger');
      return;
    }

    const formData = new FormData();
    formData.append('version_file', newVersionFile);
    formData.append('notes', newVersionNotes);

    if (currentUser) {
      formData.append('uploaded_by', currentUser.id);
    }

    try {
      await authAxios.post(`documents/${selectedDocumentForVersions.id}/versions/`, formData);
      showToast('New document version uploaded successfully!', 'success');
      setNewVersionFile(null);
      setNewVersionNotes('');
      const response = await authAxios.get(`documents/${selectedDocumentForVersions.id}/versions/`);
      setDocumentVersions(response.data);
      fetchDocuments();
    } catch (error) {
      console.error("Frontend: Error uploading new version:", error);
      const errorMessage = error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Failed to upload new document version.';
      showToast(errorMessage, 'danger');
    }
  }, [newVersionFile, newVersionNotes, selectedDocumentForVersions, authAxios, fetchDocuments, setDocumentVersions, currentUser, showToast]);

  // --- Inline Diffing State & Helpers ---
  const [selectedVersionsForCompare, setSelectedVersionsForCompare] = useState([]); // store version ids
  const [diffHtml, setDiffHtml] = useState('');
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  // Side-by-side diff state
  const [diffMode, setDiffMode] = useState('inline'); // 'inline' | 'side'
  const [diffRows, setDiffRows] = useState([]); // for side-by-side table rows

  const toggleVersionSelection = useCallback((versionId) => {
    setSelectedVersionsForCompare(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      }
      if (prev.length >= 2) {
        // Only allow two selections
        return [prev[1], versionId];
      }
      return [...prev, versionId];
    });
  }, []);

  const resetDiffState = useCallback(() => {
    setSelectedVersionsForCompare([]);
    setDiffHtml('');
    setShowDiff(false);
  }, []);

  // Basic line-level diff algorithm (LCS-based) for textual content
  const computeLineDiffHtml = (oldText, newText) => {
    const oldLines = oldText.split(/\r?\n/);
    const newLines = newText.split(/\r?\n/);
    const n = oldLines.length;
    const m = newLines.length;
    const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        if (oldLines[i - 1] === newLines[j - 1]) dp[i][j] = dp[i - 1][j - 1] + 1; else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
    const diffSegments = [];
    let i = n, j = m;
    while (i > 0 && j > 0) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        diffSegments.push({ type: 'common', text: oldLines[i - 1] });
        i--; j--;
      } else if (dp[i - 1][j] >= dp[i][j - 1]) {
        diffSegments.push({ type: 'removed', text: oldLines[i - 1] });
        i--;
      } else {
        diffSegments.push({ type: 'added', text: newLines[j - 1] });
        j--;
      }
    }
    while (i > 0) { diffSegments.push({ type: 'removed', text: oldLines[i - 1] }); i--; }
    while (j > 0) { diffSegments.push({ type: 'added', text: newLines[j - 1] }); j--; }
    diffSegments.reverse();
    const html = diffSegments.map(seg => {
      if (seg.type === 'common') return `<div class="diff-line">${escapeHtml(seg.text)}</div>`;
      if (seg.type === 'added') return `<div class="diff-line bg-green-50"><span class="text-green-700 font-semibold">+ </span>${escapeHtml(seg.text)}</div>`;
      if (seg.type === 'removed') return `<div class="diff-line bg-red-50"><span class="text-red-700 font-semibold">- </span>${escapeHtml(seg.text)}</div>`;
      return '';
    }).join('');
    return html;
  };

  const escapeHtml = (unsafe) => {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const fetchVersionText = async (url) => {
    const res = await fetch(url);
    const contentType = res.headers.get('Content-Type') || '';
    if (!contentType.includes('text') && !contentType.includes('json') && !contentType.includes('html')) {
      throw new Error('Unsupported file type for diff (only text/html/json).');
    }
    return await res.text();
  };

  const generateDiff = useCallback(async () => {
    if (selectedVersionsForCompare.length !== 2) return;
    setLoadingDiff(true);
    setDiffHtml('');
    setDiffRows([]);
    try {
      const v1 = documentVersions.find(v => v.id === selectedVersionsForCompare[0]);
      const v2 = documentVersions.find(v => v.id === selectedVersionsForCompare[1]);
      if (!v1 || !v2) throw new Error('Versions not found');
      const [oldText, newText] = await Promise.all([
        fetchVersionText(v1.version_file),
        fetchVersionText(v2.version_file)
      ]);
      const html = computeLineDiffHtml(oldText, newText);
      setDiffHtml(html);
      // Build side-by-side rows
      const rows = computeSideBySideRows(oldText, newText);
      setDiffRows(rows);
      setShowDiff(true);
    } catch (e) {
      console.error('Diff error:', e);
      showToast(e.message || 'Failed to generate diff', 'danger');
      setShowDiff(false);
    } finally {
      setLoadingDiff(false);
    }
  }, [selectedVersionsForCompare, documentVersions, showToast]);

  // Side-by-side diff builder (line-level)
  const computeSideBySideRows = (oldText, newText) => {
    const oldLines = oldText.split(/\r?\n/);
    const newLines = newText.split(/\r?\n/);
    const n = oldLines.length;
    const m = newLines.length;
    const dp = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        if (oldLines[i - 1] === newLines[j - 1]) dp[i][j] = dp[i - 1][j - 1] + 1; else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
    const segments = [];
    let i = n, j = m;
    while (i > 0 && j > 0) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        segments.push({ type: 'common', left: oldLines[i - 1], right: newLines[j - 1] });
        i--; j--;
      } else if (dp[i - 1][j] >= dp[i][j - 1]) {
        segments.push({ type: 'removed', left: oldLines[i - 1], right: '' });
        i--;
      } else {
        segments.push({ type: 'added', left: '', right: newLines[j - 1] });
        j--;
      }
    }
    while (i > 0) { segments.push({ type: 'removed', left: oldLines[i - 1], right: '' }); i--; }
    while (j > 0) { segments.push({ type: 'added', left: '', right: newLines[j - 1] }); j--; }
    segments.reverse();
    return segments;
  };

  const handleRestoreDocumentVersion = useCallback(async (versionId) => {
    if (!selectedDocumentForVersions) return;
    try {
      await authAxios.post(`documents/${selectedDocumentForVersions.id}/restore_version/`, { version_id: versionId });
      showToast('Version restored successfully as latest.', 'success');
      const response = await authAxios.get(`documents/${selectedDocumentForVersions.id}/versions/`);
      setDocumentVersions(response.data);
      fetchDocuments();
    } catch (error) {
      console.error('Restore error:', error);
      const msg = error.response?.data?.detail || 'Failed to restore version.';
      showToast(msg, 'danger');
    }
  }, [authAxios, selectedDocumentForVersions, setDocumentVersions, fetchDocuments, showToast]);

  const handleVerifyDocument = async (documentId) => {
    try {
      await authAxios.post(`documents/${documentId}/verify/`);
      showToast('Document verified successfully!', 'success');
      fetchDocuments(); 
    } catch (error) {
      console.error('Error verifying document:', error.response ? error.response.data : error);
      let errorMessage = 'Failed to verify document.';
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      }
      showToast(errorMessage, 'danger');
    }
  };

  const handleShareDocument = useCallback(async (documentId) => {
    try {
      const res = await authAxios.post(`documents/${documentId}/share/`);
      setShareLink(res.data.share_link);
      setShowShareModal(true);
    } catch (error) {
      console.error("Error generating share link:", error);
      showToast('Failed to generate share link.', 'danger');
    }
  }, [authAxios, showToast]);

  const openDeleteModal = (doc) => {
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (documentToDelete) {
      handleDelete('documents', documentToDelete.id, 'Document', fetchDocuments);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteDocumentVersion = useCallback(async (versionId) => {
    // Replaced window.confirm with a non-blocking confirmation for better UX if possible
    try {
        await authAxios.delete(`document-versions/${versionId}/`);
        showToast('Document version deleted successfully!', 'success');
        if (selectedDocumentForVersions) {
            const response = await authAxios.get(`documents/${selectedDocumentForVersions.id}/versions/`);
            setDocumentVersions(response.data);
            fetchDocuments();
        } else {
            fetchDocumentVersions();
        }
    } catch (error) {
        console.error("Frontend: Error deleting Document Version:", error);
        const errorMessage = error.response?.data?.detail || 'Failed to delete document version.';
        showToast(errorMessage, 'danger');
    }
  }, [authAxios, fetchDocuments, fetchDocumentVersions, selectedDocumentForVersions, setDocumentVersions, showToast]);

  const openGenerateDocumentModal = (templateId = '') => {
      setSelectedTemplate(templateId);
      setGenerationClientId('');
      setGenerationCaseId('');
      setGenerationDebtRecoveryRequestId('');
      setShowGenerateDocumentModal(true);
      setWizardStep(1);
      setDetectedPlaceholders([]);
      setPreviewHtml('');
      setOverrideToday('');
  };

  const handleGenerateDocument = useCallback(async () => {
    if (!selectedTemplate || !generationClientId) {
      showToast('Please select a template and a client.', 'danger');
      return;
    }
    setGeneratingDocument(true);
    try {
      const payload = {
        client_id: generationClientId,
      };
      if (generationCaseId) {
        payload.case_id = generationCaseId;
      }
      if (generationDebtRecoveryRequestId) {
        payload.debt_recovery_request_id = generationDebtRecoveryRequestId;
      }
      if (generationSelectedService) {
        payload.selected_service = generationSelectedService;
      }
      // Include any user-provided placeholder overrides so server generation uses them
      if (placeholderOverrides && Object.keys(placeholderOverrides).length > 0) {
        payload.placeholder_overrides = placeholderOverrides;
      }
      if (overrideToday) {
        payload.override_today = overrideToday;
      }

      const response = await authAxios.post(
        `document-templates/${selectedTemplate}/generate_document/`,
        payload
      );
      showToast('Document generated successfully!', 'success');
      fetchDocuments();
      closeGenerateDocumentModal();
    } catch (error) {
      console.error('Error generating document:', error);
      showToast('Error generating document.', 'danger');
    } finally {
      setGeneratingDocument(false);
    }
  }, [authAxios, selectedTemplate, generationClientId, generationCaseId, generationDebtRecoveryRequestId, fetchDocuments, closeGenerateDocumentModal, showToast]);

  // --- Drafting Wizard Helpers ---
  const selectedTemplateObj = useMemo(() => {
    return documentTemplates.find(t => String(t.id) === String(selectedTemplate));
  }, [documentTemplates, selectedTemplate]);

  useEffect(() => {
    // Populate generationTemplateServices from template.services, services_list, or by fetching the firm's services
    let mounted = true;
    const fillServices = async () => {
      if (!selectedTemplateObj) {
        if (mounted) setGenerationTemplateServices([]);
        return;
      }
      if (Array.isArray(selectedTemplateObj.services) && selectedTemplateObj.services.length > 0) {
        if (mounted) setGenerationTemplateServices(selectedTemplateObj.services);
        return;
      }
      if (selectedTemplateObj.services_list) {
        if (mounted) setGenerationTemplateServices(String(selectedTemplateObj.services_list).split(',').map(s => s.trim()).filter(Boolean));
        return;
      }
      // Try fetching firm services via API if template references a firm id
      const firmId = selectedTemplateObj.firm;
      if (firmId && authAxios) {
        try {
          const res = await authAxios.get(`/firms/${firmId}/`);
          const services = res.data?.services || [];
          if (mounted) setGenerationTemplateServices(services);
          return;
        } catch (e) {
          console.warn('Failed to fetch firm services for template', e);
        }
      }
      if (mounted) setGenerationTemplateServices([]);
    };
    fillServices();
    return () => { mounted = false; };
  }, [selectedTemplateObj, authAxios]);

  const extractRawTemplate = useCallback(() => {
    if (!selectedTemplateObj) return null;
    let raw = selectedTemplateObj.template_content;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (_) {}
    }
    return raw;
  }, [selectedTemplateObj]);

  const computeHtmlFromTemplate = useCallback(() => {
    const raw = extractRawTemplate();
    if (!raw) return '';
    // Expect Draft.js raw { blocks, entityMap }
    try {
      return draftToHtml(raw);
    } catch (e) {
      console.warn('Failed to convert draftjs to html for preview', e);
      return '';
    }
  }, [extractRawTemplate]);

  const detectPlaceholders = useCallback((html) => {
    if (!html) return [];
    // Match placeholders like {{ client.first_name }}, {{ case.partner.first_name }}, {{ firm.services_list }}, {{ today }}
    const regex = /{{\s*(client(?:\.[a-zA-Z0-9_]+)+|case(?:\.[a-zA-Z0-9_]+)+|firm(?:\.[a-zA-Z0-9_]+)+|services(?:\.[a-zA-Z0-9_]+)+|today)\s*}}/g;
    const found = new Set();
    let m;
    while ((m = regex.exec(html)) !== null) {
      found.add(m[1]);
    }
    return Array.from(found);
  }, []);

  const resolveVariableValue = useCallback((variable) => {
    if (!variable) return '';
    if (variable === 'today') {
      return overrideToday || new Date().toISOString().split('T')[0];
    }
    const parts = variable.split('.');
    if (parts.length < 2) return '';
    const [obj, ...rest] = parts; // support nested like case.partner.first_name

    const traverse = (source, keys) => {
      if (!source) return '';
      let cur = source;
      for (const k of keys) {
        if (cur === null || cur === undefined) return '';
        // if cur is an array and k looks like a numeric index
        if (Array.isArray(cur) && !isNaN(k)) {
          cur = cur[parseInt(k, 10)];
        } else {
          cur = cur[k];
        }
      }
      // If final value is an object, try to stringify reasonable fields
      if (typeof cur === 'object') return JSON.stringify(cur);
      return cur === undefined || cur === null ? '' : String(cur);
    };

    if (obj === 'client') {
      const c = clients.find(c => String(c.id) === String(generationClientId));
      // Special-case: client.trust_account_name -> use first trust account if available
      if (rest.length === 1 && rest[0] === 'trust_account_name') {
        if (c && Array.isArray(c.trust_accounts) && c.trust_accounts.length > 0) {
          return c.trust_accounts[0].account_name || '';
        }
        return '';
      }
      return traverse(c, rest);
    }
    if (obj === 'case') {
      const k = cases.find(ca => String(ca.id) === String(generationCaseId));
      // First try direct nested traversal (case.partner.first_name)
      let val = traverse(k, rest);
      if (val) return val;
      // If backend exposes partner as flattened fields (partner_first_name), try that
      if (rest.length >= 2 && rest[0] === 'partner') {
        const flatKey = `partner_${rest[1]}`;
        if (k && (k[flatKey] !== undefined && k[flatKey] !== null)) return String(k[flatKey]);
        // If partner details returned under partner_details nested object
        if (k && k.partner_details) {
          return traverse(k.partner_details, rest.slice(1));
        }
      }
      return '';
    }
    if (obj === 'firm') {
      // Special handling for firm placeholders.
      // If template or state provides services, return them for services_list.
      if (rest.length === 1 && rest[0] === 'services_list') {
        if (generationTemplateServices && generationTemplateServices.length > 0) {
          // generationTemplateServices may be array of objects or names; include price when present
          const names = generationTemplateServices.map(s => {
            if (!s) return '';
            if (typeof s === 'string') return s;
            const pname = s.name || s.title || '';
            return pname + (s.price !== undefined && s.price !== null ? ' — ' + formatPrice(s.price) : '');
          }).filter(Boolean);
          return names.join(', ');
        }
        return '';
      }
      // Attempt to read from editingTemplate.firm object if available, else currentUser.firm
      let firmObj = null;
      if (editingTemplate && typeof editingTemplate.firm === 'object') firmObj = editingTemplate.firm;
      if (!firmObj && currentUser && typeof currentUser.firm === 'object') firmObj = currentUser.firm;
      return traverse(firmObj, rest);
    }
    if (obj === 'services') {
      // services.selected may be managed in state; provide blank fallback
      if (rest[0] === 'selected') {
        // If generationTemplateServices contains objects, try to resolve selected service to include price
        if (generationTemplateServices && generationTemplateServices.length > 0) {
          // Attempt to match by id or name
          const found = generationTemplateServices.find(s => {
            if (!s) return false;
            const sel = generationSelectedService;
            if (!sel) return false;
            // compare by id (string or number)
            if ((s.id !== undefined && String(s.id) === String(sel)) || (s.name && String(s.name) === String(sel))) return true;
            // if option value used name with trimming
            return false;
          });
          if (found) {
            return `${found.name}${found.price !== undefined && found.price !== null ? ' — ' + formatPrice(found.price) : ''}`;
          }
        }
        return generationSelectedService || '';
      }
    }
    return '';
  }, [clients, cases, generationClientId, generationCaseId, overrideToday, generationSelectedService, editingTemplate, currentUser]);

  const getValueForPlaceholder = useCallback((ph) => {
    if (placeholderOverrides && Object.prototype.hasOwnProperty.call(placeholderOverrides, ph)) {
      return placeholderOverrides[ph];
    }
    return resolveVariableValue(ph) || '';
  }, [placeholderOverrides, resolveVariableValue]);

  const buildPreviewHtml = useCallback(() => {
    const baseHtml = computeHtmlFromTemplate();
    if (!baseHtml) return '';
    let rendered = baseHtml;
    detectedPlaceholders.forEach(ph => {
      const value = getValueForPlaceholder(ph);
      const escaped = ph.split('.').join('\\.');
      const pattern = new RegExp('{{\\s*' + escaped + '\\s*}}', 'g');
      rendered = rendered.replace(pattern, value || '');
    });
    // Basic logo replacement for preview
    rendered = rendered.replace('[LOGO]', '<span style="font-weight:bold">[Logo]</span>');
    setPreviewHtml(rendered);
  }, [computeHtmlFromTemplate, detectedPlaceholders, resolveVariableValue]);

  useEffect(() => {
    if (wizardStep === 2 && selectedTemplateObj) {
      const html = computeHtmlFromTemplate();
      const phs = detectPlaceholders(html);
      setDetectedPlaceholders(phs);
    }
  }, [wizardStep, selectedTemplateObj, computeHtmlFromTemplate, detectPlaceholders]);

  useEffect(() => {
    if (wizardStep === 3 || wizardStep === 4) {
      buildPreviewHtml();
    }
  }, [wizardStep, buildPreviewHtml, overrideToday, generationClientId, generationCaseId, placeholderOverrides]);

  const canContinueFromStep1 = useMemo(() => selectedTemplate && generationClientId, [selectedTemplate, generationClientId]);
  const canContinueFromStep2 = useMemo(() => true, []); // All placeholders optional for now

  const nextStep = () => setWizardStep(s => Math.min(4, s + 1));
  const prevStep = () => setWizardStep(s => Math.max(1, s - 1));

  const handleInsertPlaceholder = useCallback((placeholderValue) => {
    const newEditorState = EditorState.push(
        templateContent,
        // FIX: Use `replaceText` instead of `insertText`.
        // `replaceText` correctly handles both collapsed selections (cursor)
        // and non-collapsed selections (highlighted text), preventing the error.
        Modifier.replaceText(
            templateContent.getCurrentContent(),
            templateContent.getSelection(),
            placeholderValue
        ),
        'insert-characters'
    );
    onEditorStateChange(newEditorState);
  }, [templateContent, onEditorStateChange]);

  const renderPreviewModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center animate__animated animate__fadeIn">
      <div className="relative p-5 border w-full max-w-4xl max-h-[85vh] shadow-lg rounded-md bg-white overflow-y-auto">
        <div className="flex justify-between items-center pb-3 border-b bg-blue-600 text-white rounded-t-lg -mx-5 -mt-5 px-5 py-3 sticky top-0 z-20">
          <h5 className="text-xl font-bold">Template Preview</h5>
          <button type="button" className="text-white hover:text-gray-200" aria-label="Close" onClick={() => setShowPreviewModal(false)}>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 prose lg:prose-xl max-w-none overflow-y-auto" style={{ maxHeight: 'calc(85vh - 100px)' }}>
          <div dangerouslySetInnerHTML={{ __html: previewContent }} />
        </div>
        <div className="flex justify-end space-x-4 mt-6 -mx-5 -mb-5 px-5 py-3 bg-gray-50 border-t rounded-b-lg sticky bottom-0">
          <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={() => setShowPreviewModal(false)}>Close</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
       <style>{`
        .RichEditor-editor .public-DraftStyleDefault-h1 {
            font-size: 2.25rem; /* text-4xl */
            font-weight: 700;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
        }
        .RichEditor-editor .public-DraftStyleDefault-h2 {
            font-size: 1.875rem; /* text-3xl */
            font-weight: 700;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
        }
        .RichEditor-editor .public-DraftStyleDefault-h3 {
            font-size: 1.5rem; /* text-2xl */
            font-weight: 700;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
        }
        .RichEditor-editor .public-DraftStyleDefault-blockquote {
            border-left: 4px solid #d1d5db; /* border-gray-300 */
            padding-left: 1rem;
            color: #4b5563; /* text-gray-600 */
            font-style: italic;
            margin: 1rem 0;
        }
        .RichEditor-editor .public-DraftStyleDefault-ul {
            list-style-type: disc;
            padding-left: 2.5rem; /* Corresponds to pl-10 */
        }
        .RichEditor-editor .public-DraftStyleDefault-ol {
            list-style-type: decimal;
            padding-left: 2.5rem; /* Corresponds to pl-10 */
        }
        .RichEditor-editor .public-DraftStyleDefault-pre {
            background-color: #f3f4f6; /* bg-gray-100 */
            font-family: monospace;
            padding: 1rem;
            border-radius: 0.375rem; /* rounded-md */
            white-space: pre-wrap;
        }
        .RichEditor-editor .public-DraftStyleDefault-li {
            margin-bottom: 0.5rem;
        }
        .prose h1 {
          font-size: 2.25rem;
        }
        .prose h2 {
          font-size: 1.875rem;
        }
        .prose h3 {
          font-size: 1.5rem;
        }
        .prose p {
          margin-top: 1.25em;
          margin-bottom: 1.25em;
        }
        .prose blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 1rem;
          font-style: italic;
        }
        .prose ul {
          list-style-type: disc;
          padding-left: 1.5rem;
        }
        .prose ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
        }
        .prose strong {
          font-weight: 700;
        }
        .prose em {
          font-style: italic;
        }
       `}</style>
      {/* Local toast UI removed in favor of global toasts (showFormMessage). */}
      {/* --- Filing Cabinet Explorer Section --- */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h5 className="text-lg font-bold text-gray-800">Filing Cabinet Explorer</h5>
          <div className="flex items-center gap-3">
            <select
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
              value={explorerCaseId}
              onChange={(e) => setExplorerCaseId(e.target.value)}
            >
              <option value="">Select a Case…</option>
              {(cases || []).map((_case) => (
                <option key={_case.id} value={_case.id}>{_case.title}</option>
              ))}
            </select>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium ${showExplorer ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setShowExplorer((v) => !v)}
            >
              {showExplorer ? 'Hide Explorer' : 'Show Explorer'}
            </button>
          </div>
        </div>
        {showExplorer && (
          <div className="p-6">
            {explorerCaseId ? (
              <DocumentExplorer authAxios={authAxios} caseId={explorerCaseId} onToast={showToast} />
            ) : (
              <p className="text-sm text-gray-500">Select a case to view its folders and documents.</p>
            )}
          </div>
        )}
      </div>
      {/* --- Document Templates Section --- */}
          <div className="flex justify-between items-center mb-6">
              <div>
                  <h2 className="text-3xl font-bold text-gray-800">Document Management</h2>
                    <div className="flex space-x-4 mt-4">
                        {permissions.canCreateTemplate && (
                            <button
                                onClick={openAddTemplateModal}
                                className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                            >
                                <i className="bi bi-plus-lg mr-2"></i> New Template
                            </button>
                        )}
                        {permissions.canCreateDocument && (
                            <button
                                onClick={() => openGenerateDocumentModal()}
                                className="bg-teal-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 flex items-center"
                            >
                                <i className="bi bi-file-earmark-text mr-2"></i> Generate Document
                            </button>
                        )}
                         {permissions.canCreateDocument && (
                            <button
                                onClick={openAddDocumentModal}
                                className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center"
                            >
                                <i className="bi bi-upload mr-2"></i> Upload Document
                            </button>
                        )}
                        {permissions.canCreateDocument && onOpenEditor && (
                            <button
                                onClick={() => onOpenEditor({ id: null, title: '', content: '' })}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
                            >
                                <i className="bi bi-pencil-square mr-2"></i> New in Editor
                            </button>
                        )}
                    </div>
              </div>
          </div>
          
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
                  <h5 className="text-lg font-bold text-gray-800">All Templates</h5>
                   <div className="flex space-x-2">
                      <button 
                          className={`px-4 py-2 rounded-md text-sm font-medium ${templatesViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} 
                          onClick={() => setTemplatesViewMode('list')}>
                          List
                      </button>
                      <button 
                          className={`px-4 py-2 rounded-md text-sm font-medium ${templatesViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} 
                          onClick={() => setTemplatesViewMode('tiles')}>
                          Tiles
                      </button>
                  </div>
              </div>
              <div className="p-6">
                  {templatesViewMode === 'list' ? (
                      <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                  <tr>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Public</th>
                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                  </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                  {documentTemplates.map((template) => (
                                      <tr key={template.id} className="hover:bg-gray-50">
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{template.title}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.description}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.document_type}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{template.is_public ? 'Yes' : 'No'}</td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-4">
                                              <button className="text-indigo-600 hover:text-indigo-900 flex items-center" onClick={() => handlePreviewTemplate(template)}><i className="bi bi-eye mr-2"></i>Preview</button>
                                              {permissions.canEditTemplate && <button className="text-yellow-600 hover:text-yellow-900 flex items-center" onClick={() => handleEditTemplate(template)}><i className="bi bi-pencil-square mr-2"></i>Edit</button>}
                                              {permissions.canCreateDocument && <button className="text-teal-600 hover:text-teal-900 flex items-center" onClick={() => openGenerateDocumentModal(template.id)}><i className="bi bi-file-earmark-text mr-2"></i>Generate</button>}
                                          </td>
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      </div>
                  ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {documentTemplates.map((template) => (
                              <div key={template.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
                                  <div className="p-6 flex-grow">
                                      <h5 className="text-lg font-bold text-gray-800">{template.title}</h5>
                                      <p className="text-sm text-gray-600 mt-2 flex-grow">{template.description}</p>
                                      <p className="text-sm text-gray-500 mt-4"><small>Type: {template.document_type}</small></p>
                                      <p className="text-sm text-gray-500"><small>Public: {template.is_public ? 'Yes' : 'No'}</small></p>
                                    </div>
                                    <div className="p-6 pt-0 mt-auto flex justify-end space-x-4">
                                        <button className="text-indigo-600 hover:text-indigo-900 flex items-center" onClick={() => handlePreviewTemplate(template)}><i className="bi bi-eye mr-2"></i>Preview</button>
                                        {permissions.canEditTemplate && <button className="text-yellow-600 hover:text-yellow-900 flex items-center" onClick={() => handleEditTemplate(template)}><i className="bi bi-pencil-square mr-2"></i>Edit</button>}
                                        {permissions.canCreateDocument && <button className="text-teal-600 hover:text-teal-900 flex items-center" onClick={() => openGenerateDocumentModal(template.id)}><i className="bi bi-file-earmark-text mr-2"></i>Generate</button>}
                                    </div>
                              </div>
                          ))}
                      </div>
                  )}
                  {(!documentTemplates || documentTemplates.length === 0) && <p className="text-center py-4 text-sm text-gray-500">No document templates available.</p>}
              </div>
          </div>
      
      {/* --- My Documents Section --- */}
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
              <h5 className="text-lg font-bold text-gray-800">My Documents</h5>
              <div className="flex space-x-2">
                  <button 
                      className={`px-4 py-2 rounded-md text-sm font-medium ${documentsViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} 
                      onClick={() => setDocumentsViewMode('list')}>
                      List
                  </button>
                  <button 
                      className={`px-4 py-2 rounded-md text-sm font-medium ${documentsViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} 
                      onClick={() => setDocumentsViewMode('tiles')}>
                      Tiles
                  </button>
              </div>
          </div>
          <div className="p-6">
              {documentsViewMode === 'list' ? (
                  <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                              <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {documents.map(doc => (
                                  <tr key={doc.id} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-2">
                                        <span>{doc.title}</span>
                                        <button
                                          type="button"
                                          onClick={() => openDocumentVersionsModal(doc)}
                                          title="View version history"
                                          className="text-xs font-semibold rounded-full px-2 py-0.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                                        >
                                          v{(doc.latest_version_number || 1)}
                                        </button>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.case_title || 'N/A'}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.client_full_name || getClientDisplayName(doc.client_details) || 'N/A'}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.is_verified ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Yes</span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">No</span>}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-4">
                                          <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openDocumentDetailsModal(doc)} title="View Details"><i className="bi bi-eye mr-1"></i>View</button>
                                          {permissions.canEditDocument && <button className="text-yellow-600 hover:text-yellow-900 flex items-center" onClick={() => openEditDocumentModal(doc)} title="Edit Document"><i className="bi bi-pencil mr-1"></i>Edit</button>}
                                          {permissions.canEditDocument && <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openDocumentVersionsModal(doc)} title="Manage Versions"><i className="bi bi-hdd-stack mr-1"></i>Versions</button>}
                                          <button className="text-teal-600 hover:text-teal-900 flex items-center" onClick={() => handleShareDocument(doc.id)} title="Share Document"><i className="bi bi-share mr-1"></i>Share</button>
                                          {onOpenEditor && <button className="text-indigo-600 hover:text-indigo-900 flex items-center" onClick={() => onOpenEditor({ id: doc.id, title: doc.title, content: doc.html_content || '' })} title="Edit in Editor"><i className="bi bi-pencil-square mr-1"></i>Editor</button>}
                                          {permissions.canDeleteDocument && <button className="text-red-600 hover:text-red-900 flex items-center" onClick={() => openDeleteModal(doc)} title="Delete Document"><i className="bi bi-trash3 mr-1"></i>Delete</button>}
                                          {permissions.canVerifyDocument && !doc.is_verified && (
                                              <button className="text-green-600 hover:text-green-900 flex items-center" onClick={() => handleVerifyDocument(doc.id)} title="Verify Document">
                                                  <i className="bi bi-check-circle mr-1"></i>Verify
                                              </button>
                                          )}
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {documents.map(doc => (
                          <div key={doc.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col relative">
                              <div className="p-6 flex-grow">
                                  <div className="flex items-start justify-between">
                                    <h5 className="text-lg font-bold text-gray-800 pr-2">{doc.title}</h5>
                                    <button
                                      type="button"
                                      onClick={() => openDocumentVersionsModal(doc)}
                                      title="View version history"
                                      className="text-xs font-semibold rounded-full px-2 py-0.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                                    >
                                      v{(doc.latest_version_number || 1)}
                                    </button>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-2"><small className="text-gray-500">Case: {doc.case_title || 'N/A'}</small></p>
                                  <p className="text-sm text-gray-600"><small className="text-gray-500">Client: {doc.client_full_name || getClientDisplayName(doc.client_details) || 'N/A'}</small></p>
                                  <p className="text-sm text-gray-600 mt-3">
                                      <strong>Status:</strong> {doc.is_verified ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 ml-2">Verified</span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 ml-2">Not Verified</span>}
                                  </p>
                                  </div>
                                  <div className="p-6 pt-0 mt-auto flex justify-end flex-wrap gap-2">
                                      <button className="text-blue-600 hover:text-blue-900 text-sm flex items-center" onClick={() => openDocumentDetailsModal(doc)} title="View Details"><i className="bi bi-eye mr-1"></i></button>
                                      {permissions.canEditDocument && <button className="text-yellow-600 hover:text-yellow-900 text-sm flex items-center" onClick={() => openEditDocumentModal(doc)} title="Edit Document"><i className="bi bi-pencil mr-1"></i></button>}
                                      {permissions.canEditDocument && <button className="text-gray-600 hover:text-gray-900 text-sm flex items-center" onClick={() => openDocumentVersionsModal(doc)} title="Manage Versions"><i className="bi bi-hdd-stack mr-1"></i></button>}
                                      <button className="text-teal-600 hover:text-teal-900 text-sm flex items-center" onClick={() => handleShareDocument(doc.id)} title="Share Document"><i className="bi bi-share mr-1"></i></button>
                                      {onOpenEditor && <button className="text-indigo-600 hover:text-indigo-900 text-sm flex items-center" onClick={() => onOpenEditor({ id: doc.id, title: doc.title, content: doc.html_content || '' })} title="Edit in Editor"><i className="bi bi-pencil-square mr-1"></i></button>}
                                      {permissions.canDeleteDocument && <button className="text-red-600 hover:text-red-900 text-sm flex items-center" onClick={() => openDeleteModal(doc)} title="Delete Document"><i className="bi bi-trash3 mr-1"></i></button>}
                                      {permissions.canVerifyDocument && !doc.is_verified && (
                                        <button className="text-green-600 hover:text-green-900 text-sm flex items-center" onClick={() => handleVerifyDocument(doc.id)} title="Verify Document"><i className="bi bi-check-circle mr-1"></i></button>
                                      )}
                                  </div>
                          </div>
                      ))}
                  </div>
              )}
              {(!documents || documents.length === 0) && <p className="text-center py-4 text-sm text-gray-500">No documents found.</p>}
          </div>
      </div>
        
          {/* All modals go here, converted to side panels */}
          {showAddDocumentModal && (
            <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showAddDocumentModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showAddDocumentModal ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                        <h5 className="text-2xl font-bold text-gray-800">Upload New Document</h5>
                        <button onClick={closeAddDocumentModal} className="text-gray-600 hover:text-gray-900">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <form onSubmit={handleDocumentSubmit} className="h-full flex flex-col">
                        <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                          <div className="">
                            <label className="block text-sm font-medium text-gray-700">Title</label>
                            <input type="text" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={newDocument.title} onChange={e => setNewDocument({ ...newDocument, title: e.target.value })} required />
                          </div>
                          <div className="">
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={newDocument.description} onChange={e => setNewDocument({ ...newDocument, description: e.target.value })}></textarea>
                          </div>
                          <div className="">
                            <label className="block text-sm font-medium text-gray-700">Document Type:</label>
                            <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={newDocument.document_type} onChange={e => setNewDocument({ ...newDocument, document_type: e.target.value })}>
                              {(documentTypeOptions || []).map(option => (
                                <option className="text-sm" key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="">
                            <label className="block text-sm font-medium text-gray-700">Associated Client</label>
                            <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={newDocument.client} onChange={e => setNewDocument({ ...newDocument, client: e.target.value, case: '' })}>
                              <option value="">None</option>
                              {(clients || []).map(client => (
                                <option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>
                              ))}
                            </select>
                          </div>
                          <div className="">
                            <label className="block text-sm font-medium text-gray-700">Associated Case</label>
                            <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={newDocument.case} onChange={e => setNewDocument({ ...newDocument, case: e.target.value })} disabled={!newDocument.client}>
                              <option value="">None</option>
                              {filteredCases.map(_case => (
                                <option key={_case.id} value={_case.id}>{_case.title}</option>
                              ))}
                            </select>
                          </div>
                          <div className="">
                            <label className="block text-sm font-medium text-gray-700">Document File (PDF, DOCX, etc.)</label>
                            <input type="file" className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" onChange={e => setNewDocument({ ...newDocument, file: e.target.files[0] })} required />
                          </div>
                        </div>
                        <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                          <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={closeAddDocumentModal}>Cancel</button>
                          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700">Upload Document</button>
                        </div>
                    </form>
                </div>
            </div>
          )}

          {showAddTemplateModal && (
            <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showAddTemplateModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showAddTemplateModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">{editingTemplate ? 'Edit Template' : 'Create New Template'}</h5>
                     <button onClick={closeAddTemplateModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                </div>
                <form onSubmit={handleTemplateSubmit} className="h-full flex flex-col">
                    <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                      <div className="">
                        <label className="block text-sm font-medium text-gray-700">Template Title</label>
                        <input type="text" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={newTemplate.title} onChange={e => setNewTemplate({ ...newTemplate, title: e.target.value })} required />
                      </div>
                      <div className="">
                        <label className="block text-sm font-medium text-gray-700">Document Type</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={newTemplate.document_type} onChange={e => setNewTemplate({ ...newTemplate, document_type: e.target.value })} required>
                          {DOCUMENT_TYPE_CHOICES.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <div className="flex items-start">
                          <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <div className="ml-3">
                            <p className="text-sm text-blue-700">
                              <strong>Logo/Letterhead:</strong> This template will use your firm's logo uploaded in the admin interface. Update your firm logo in the admin settings to change the logo on all templates and invoices.
                            </p>
                            {editingTemplate && editingTemplate.logo_url && (
                              <div className="mt-2">
                                <img src={editingTemplate.logo_url} alt="Firm Logo" className="max-w-[100px] max-h-[100px]" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" id="isPublicCheck" checked={newTemplate.is_public} onChange={e => setNewTemplate({ ...newTemplate, is_public: e.target.checked })} />
                        <label className="ml-2 block text-sm text-gray-900" htmlFor="isPublicCheck">Public Template (can be used by all users)</label>
                      </div>
                      <div className="">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" rows="3" value={newTemplate.description} onChange={e => setNewTemplate({ ...newTemplate, description: e.target.value })} />
                      </div>
                      <div className="">
                        <label className="block text-sm font-medium text-gray-700">Template Content</label>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-700">Insert Placeholder:</label>
                          <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" onChange={e => handleInsertPlaceholder(e.target.value)} value="">
                            <option value="">Select a placeholder...</option>
                            {dynamicPlaceholders.map((p, index) => (<option key={index} value={p.value}>{p.label}</option>))}
                          </select>
                        </div>
                        <div className="RichEditor-root bg-gray-50 border border-gray-300 rounded-md p-4" onClick={focusEditor}>
                           <RichTextEditorToolbar editorState={templateContent} onToggleBlockType={onToggleBlockType} onToggleInlineStyle={onToggleInlineStyle} setTemplateContent={onEditorStateChange} />
                            <div className="RichEditor-editor min-h-[200px] bg-white p-2 rounded">
                                <Editor editorState={templateContent} onChange={onEditorStateChange} handleKeyCommand={handleKeyCommand} placeholder="Start writing your template..." ref={editorRef} blockStyleFn={getBlockStyle}/>
                            </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                      <button type="button" className="bg-indigo-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-indigo-600" onClick={handleNewTemplatePreview}>Preview</button>
                      <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={closeAddTemplateModal}>Cancel</button>
                      <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700">Save Template</button>
                    </div>
                  </form>
                </div>
              </div>
          )}

          {editDocument && showEditDocumentModal && (
             <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showEditDocumentModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showEditDocumentModal ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                        <h5 className="text-2xl font-bold text-gray-800">Edit Document</h5>
                         <button onClick={closeEditDocumentModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                    </div>
                  <form onSubmit={handleEditDocumentSubmit} className="h-full flex flex-col">
                    <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                      <div className="text-sm">
                        <label className="block text-sm font-medium text-gray-700">Title</label>
                        <input type="text" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={editDocument.title} onChange={e => setEditDocument({ ...editDocument, title: e.target.value })} required/>
                      </div>
                      <div className="text-sm">
                        <label className="block text-sm font-medium text-gray-700">Document Type</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={editDocument.document_type} onChange={e => setEditDocument({ ...editDocument, document_type: e.target.value})}>
                          <option className="text-sm" value="">Select Type</option>
                          {(documentTypeOptions || []).map(option => (<option className="text-sm" key={option.value} value={option.value}>{option.label}</option>))}
                        </select>
                      </div>
                      <div className="text-sm">
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={editDocument.description} onChange={e => setEditDocument({ ...editDocument, description: e.target.value })} />
                      </div>
                      <div className="text-sm">
                        <label className="block text-sm font-medium text-gray-700">Associated Client</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={editDocument.client} onChange={e => setEditDocument({ ...editDocument, client: e.target.value })}>
                          <option className="text-sm" value="">Select Client</option>
                          {(clients || []).map(client => (<option className="text-sm" key={client.id} value={client.id}>{getClientDisplayName(client)}</option>))}
                        </select>
                      </div>
                      <div className="text-sm">
                        <label className="block text-sm font-medium text-gray-700">Associated Case</label>
                        <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={editDocument.case} onChange={e => setEditDocument({ ...editDocument, case: e.target.value })}>
                          <option className="text-sm" value="">Select Case</option>
                          {(cases || []).map(_case => (<option className="text-sm" key={_case.id} value={_case.id}>{_case.title}</option>))}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                      <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={closeEditDocumentModal}>Cancel</button>
                      <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700">Save Changes</button>
                    </div>
                  </form>
                </div>
              </div>
          )}

          {showDocumentVersionsModal && selectedDocumentForVersions && (
             <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showDocumentVersionsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
               <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showDocumentVersionsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">Versions for: {selectedDocumentForVersions.title}</h5>
                     <button onClick={closeDocumentVersionsModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                  </div>
                  <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                    <div className="space-y-3">
                      {Array.isArray(documentVersions) && documentVersions.length > 0 ? (
                        documentVersions.map((version) => {
                          const checked = selectedVersionsForCompare.includes(version.id);
                          return (
                            <div key={version.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-md shadow-sm">
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  checked={checked}
                                  onChange={() => toggleVersionSelection(version.id)}
                                  title="Select for diff"
                                />
                                <div>
                                  <h6 className="font-semibold text-gray-800 flex items-center gap-2">Version {version.version_number}
                                    {checked && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Selected</span>}
                                  </h6>
                                  <p className="text-sm text-gray-600">Uploaded by: {version.uploaded_by_username} on {format(parseISO(version.uploaded_at), 'PPP p')}</p>
                                  {version.notes && (<p className="text-xs text-gray-500">Notes: {version.notes}</p>)}
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <a href={version.version_file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-gray-200" title="Download Version"><i className="bi bi-download"></i></a>
                                {permissions.canDeleteDocument && (<button className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-gray-200" onClick={() => handleDeleteDocumentVersion(version.id)} title="Delete Version"><i className="bi bi-trash"></i></button>)}
                                {permissions.canEditDocument && (<button className="text-green-600 hover:text-green-900 p-2 rounded-full hover:bg-gray-200" onClick={() => handleRestoreDocumentVersion(version.id)} title="Restore as Latest"><i className="bi bi-arrow-counterclockwise"></i></button>)}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="bg-blue-100 text-blue-800 p-3 rounded-md text-center" role="alert">No versions found for this document.</div>
                      )}
                      {/* Diff Controls */}
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          disabled={selectedVersionsForCompare.length !== 2 || loadingDiff}
                          onClick={generateDiff}
                          className="px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white disabled:opacity-50 hover:bg-indigo-700"
                        >
                          {loadingDiff ? 'Generating Diff…' : 'Compare Selected'}
                        </button>
                        {showDiff && (
                          <button
                            type="button"
                            onClick={() => setShowDiff(false)}
                            className="px-3 py-2 rounded-md text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
                          >Hide Diff</button>
                        )}
                        {(selectedVersionsForCompare.length > 0 || showDiff) && (
                          <button
                            type="button"
                            onClick={resetDiffState}
                            className="px-3 py-2 rounded-md text-sm bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                          >Reset Selection</button>
                        )}
                        {showDiff && (
                          <div className="flex items-center gap-2 ml-auto">
                            <button
                              type="button"
                              onClick={() => setDiffMode('inline')}
                              className={`px-3 py-2 rounded-md text-xs font-medium ${diffMode==='inline' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            >Inline</button>
                            <button
                              type="button"
                              onClick={() => setDiffMode('side')}
                              className={`px-3 py-2 rounded-md text-xs font-medium ${diffMode==='side' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            >Side-by-Side</button>
                          </div>
                        )}
                      </div>
                      {showDiff && diffMode === 'inline' && (
                        <div className="mt-6 border rounded-md p-4 bg-white max-h-80 overflow-auto">
                          <h6 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            Inline Diff
                            <span className="text-xs font-normal text-gray-500">(+ additions / - removals)</span>
                          </h6>
                          <div className="text-xs font-mono leading-relaxed" dangerouslySetInnerHTML={{ __html: diffHtml || '<p class="text-gray-400">No diff generated.</p>' }} />
                        </div>
                      )}
                      {showDiff && diffMode === 'side' && (
                        <div className="mt-6 border rounded-md p-4 bg-white max-h-80 overflow-auto">
                          <h6 className="text-sm font-semibold mb-3 flex items-center gap-2">
                            Side-by-Side Diff
                            <span className="text-xs font-normal text-gray-500">Green = Added, Red = Removed</span>
                          </h6>
                          <table className="w-full text-xs font-mono border-collapse">
                            <thead>
                              <tr>
                                <th className="border-b py-1 text-left w-1/2">Old Version</th>
                                <th className="border-b py-1 text-left w-1/2">New Version</th>
                              </tr>
                            </thead>
                            <tbody>
                              {diffRows.length === 0 && (
                                <tr><td colSpan={2} className="py-2 text-gray-400">No diff generated.</td></tr>
                              )}
                              {diffRows.map((r, idx) => (
                                <tr key={idx} className={
                                  r.type === 'common' ? '' : r.type === 'added' ? 'bg-green-50' : 'bg-red-50'
                                }>
                                  <td className="align-top whitespace-pre-wrap border-r px-2 py-0.5">
                                    {r.left !== '' ? (
                                      <span className={r.type==='removed' ? 'text-red-700 font-semibold' : ''}>{r.left}</span>
                                    ) : ''}
                                  </td>
                                  <td className="align-top whitespace-pre-wrap px-2 py-0.5">
                                    {r.right !== '' ? (
                                      <span className={r.type==='added' ? 'text-green-700 font-semibold' : ''}>{r.right}</span>
                                    ) : ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <hr className="my-4 border-gray-300" />
                    <h6 className="text-lg font-bold text-gray-800 mb-3">Upload New Version</h6>
                    <form onSubmit={handleUploadNewVersion} className="space-y-4">
                      <div>
                        <label htmlFor="newVersionFile" className="block text-sm font-medium text-gray-700">Version File</label>
                        <input type="file" className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" id="newVersionFile" onChange={handleNewVersionFileChange} required/>
                      </div>
                      <div>
                        <label htmlFor="newVersionNotes" className="block text-sm font-medium text-gray-700">Version Notes (Optional)</label>
                        <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" id="newVersionNotes" rows="2" value={newVersionNotes} onChange={handleNewVersionNotesChange}></textarea>
                      </div>
                      <div className="flex justify-end">
                          <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 flex items-center"><i className="bi bi-upload mr-2"></i> Upload</button>
                      </div>
                    </form>
                  </div>
                   <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                    <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={closeDocumentVersionsModal}>Close</button>
                  </div>
                </div>
              </div>
          )}

          {showGenerateDocumentModal && (
             <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showGenerateDocumentModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
               <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showGenerateDocumentModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">Drafting Wizard</h5>
                     <button onClick={closeGenerateDocumentModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                  </div>
                  <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                    {/* Step indicator */}
                    <div className="flex items-center gap-2 text-xs font-medium">
                      {[1,2,3,4].map((s, idx) => (
                        <React.Fragment key={s}>
                          {idx > 0 && <span className="mx-2 text-gray-400">→</span>}
                          <span className={`px-2 py-1 rounded-full ${wizardStep===s?'bg-blue-600 text-white':'bg-gray-200 text-gray-700'}`}>Step {s}</span>
                        </React.Fragment>
                      ))}
                    </div>
                    {wizardStep === 1 && (
                      <div className="space-y-4">
                        <p className="text-gray-700">Select a template, client, and optional case to continue.</p>
                        <div>
                          <label htmlFor="template-select" className="block text-sm font-medium text-gray-700">Template</label>
                          <select id="template-select" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}>
                            <option value="">Select a template...</option>
                            {documentTemplates.map(template => (<option key={template.id} value={template.id}>{template.title}</option>))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="client-select-generate" className="block text-sm font-medium text-gray-700">Client</label>
                          <select id="client-select-generate" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={generationClientId} onChange={e => { setGenerationClientId(e.target.value); setGenerationCaseId(''); setGenerationDebtRecoveryRequestId(''); }}>
                            <option value="">Select a Client...</option>
                            {Array.isArray(clients) ? clients.map(client => (<option key={client.id} value={client.id}>{getClientDisplayName(client)}</option>)) : null}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="case-select-generate" className="block text-sm font-medium text-gray-700">Case (Optional)</label>
                          <select id="case-select-generate" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={generationCaseId} onChange={e => setGenerationCaseId(e.target.value)} disabled={!generationClientId}>
                            <option value="">Select a Case...</option>
                            {generationFilteredCases.map(_case => (<option key={_case.id} value={_case.id}>{_case.title}</option>))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="drr-select-generate" className="block text-sm font-medium text-gray-700">Debt Recovery Request (Optional)</label>
                          <select
                            id="drr-select-generate"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                            value={generationDebtRecoveryRequestId}
                            onChange={e => setGenerationDebtRecoveryRequestId(e.target.value)}
                            disabled={!generationClientId || generationDebtRecoveryLoading}
                          >
                            <option value="">Select a DRR...</option>
                            {generationFilteredDebtRecoveryRequests.map((req) => (
                              <option key={req.id} value={req.id}>
                                {(req.reference || `DR-${req.id}`)} — {req.title || 'Untitled'} ({req.status || 'INTAKE'})
                              </option>
                            ))}
                          </select>
                          {generationDebtRecoveryLoading && <div className="text-xs text-gray-500 mt-1">Loading DRRs…</div>}
                          {generationDebtRecoveryError && <div className="text-xs text-red-600 mt-1">{generationDebtRecoveryError}</div>}
                        </div>
                        <div>
                          <label htmlFor="service-select-generate" className="block text-sm font-medium text-gray-700">Service (Optional)</label>
                          {generationTemplateServices && generationTemplateServices.length > 0 ? (
                            <select id="service-select-generate" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={generationSelectedService} onChange={e => setGenerationSelectedService(e.target.value)}>
                              <option value="">Select a service...</option>
                              {generationTemplateServices.map((s,i) => (
                                    // service might be object or string; include price when available
                                    typeof s === 'string' ? (
                                      <option key={i} value={s.trim()}>{s.trim()}</option>
                                    ) : (
                                      <option key={s.id || i} value={s.id || s.name || i}>{`${s.name || s.title || s.id}${s.price !== undefined && s.price !== null ? ' — ' + formatPrice(s.price) : ''}`}</option>
                                    )
                              ))}
                            </select>
                          ) : (
                            <input id="service-select-generate" placeholder="Optional: enter service name" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" value={generationSelectedService} onChange={e => setGenerationSelectedService(e.target.value)} />
                          )}
                        </div>
                      </div>
                    )}
                    {wizardStep === 2 && (
                      <div className="space-y-4">
                        <p className="text-gray-700">Detected variables from the template. These will be auto-filled where possible. You can override today's date.</p>
                        <div className="space-y-2">
                          {detectedPlaceholders.length === 0 && <p className="text-sm text-gray-500">No dynamic placeholders detected.</p>}
                          {detectedPlaceholders.map(ph => {
                            const resolved = resolveVariableValue(ph) || '';
                            const overridden = Object.prototype.hasOwnProperty.call(placeholderOverrides, ph);
                            const displayValue = overridden ? placeholderOverrides[ph] : (resolved || '');
                            return (
                              <div key={ph} className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm bg-gray-50 rounded px-3 py-2 gap-2">
                                <div className="flex-1">
                                  <div className="font-mono text-gray-600">{`{{ ${ph} }}`}</div>
                                  <div className="text-xs text-gray-500">Source: {resolved ? 'API' : 'Not provided'}</div>
                                </div>
                                <div className="w-full sm:w-1/2">
                                  <input
                                    type="text"
                                    value={displayValue}
                                    placeholder={resolved ? '' : '(empty — enter value to override)'}
                                    onChange={e => {
                                      const v = e.target.value;
                                      setPlaceholderOverrides(prev => {
                                        const next = { ...prev };
                                        if (v === '') delete next[ph]; else next[ph] = v;
                                        return next;
                                      });
                                    }}
                                    className="w-full px-3 py-2 border rounded"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Override Today (Optional)</label>
                          <input type="date" value={overrideToday} onChange={e => setOverrideToday(e.target.value)} className="mt-1 w-full border px-3 py-2 rounded" />
                        </div>
                      </div>
                    )}
                    {wizardStep === 3 && (
                      <div className="space-y-4">
                        <p className="text-gray-700">Preview (client/case values substituted). Review content below — you can open a larger preview in the next step before finalizing.</p>
                        <div className="border rounded p-3 bg-white h-64 overflow-auto" dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-sm text-gray-500">No preview available.</p>' }} />
                        <div className="flex justify-end">
                          <button type="button" className="px-4 py-2 rounded bg-blue-600 text-white" onClick={() => setWizardStep(4)}>Open Full Preview</button>
                        </div>
                      </div>
                    )}
                    {wizardStep === 4 && (
                      <div className="space-y-4">
                        <p className="text-gray-700">Final Preview — confirm and generate the PDF. This preview represents the document content that will be rendered server-side.</p>
                        <div className="border rounded p-3 bg-white h-96 overflow-auto" dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-sm text-gray-500">No preview available.</p>' }} />
                        {generatingDocument && (
                          <div className="flex items-center mt-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mr-2" />
                            <span className="text-gray-700 text-sm">Generating document...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center p-4 border-t bg-gray-50 sticky bottom-0">
                    <div className="flex gap-2">
                      <button type="button" className="px-3 py-2 text-sm rounded border" onClick={closeGenerateDocumentModal} disabled={generatingDocument}>Cancel</button>
                      {wizardStep > 1 && <button type="button" className="px-3 py-2 text-sm rounded border" onClick={prevStep} disabled={generatingDocument}>Back</button>}
                    </div>
                    <div className="flex gap-2">
                      {wizardStep < 4 && (
                        <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50" onClick={nextStep} disabled={generatingDocument || (wizardStep===1 && !canContinueFromStep1) || (wizardStep===2 && !canContinueFromStep2)}>Next</button>
                      )}
                      {wizardStep === 4 && (
                        <button type="button" className="bg-teal-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50" onClick={handleGenerateDocument} disabled={generatingDocument || !selectedTemplate || !generationClientId}>Generate PDF</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
          )}

          {showDocumentDetailsModal && selectedDocumentDetails && (
             <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showDocumentDetailsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <div className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showDocumentDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">{selectedDocumentDetails.title}</h5>
                     <button onClick={closeDocumentDetailsModal} className="text-gray-600 hover:text-gray-900"><i className="bi bi-x-lg"></i></button>
                  </div>
                  <div className="p-6 space-y-4 overflow-y-auto" style={{ height: 'calc(100vh - 70px)' }}>
                    <ul className="divide-y divide-gray-200">
                      <li className="py-2"><strong>Description:</strong> {selectedDocumentDetails.description || 'N/A'}</li>
                      <li className="py-2"><strong>Document Type:</strong> {selectedDocumentDetails.document_type}</li>
                      <li className="py-2"><strong>Associated Client:</strong> {selectedDocumentDetails.client_full_name || getClientDisplayName(selectedDocumentDetails.client_details) || 'N/A'}</li>
                      <li className="py-2"><strong>Associated Case:</strong> {selectedDocumentDetails.case_title || 'N/A'}</li>
                      <li className="py-2"><strong>Uploaded By:</strong> {selectedDocumentDetails.uploaded_by_username}</li>
                      <li className="py-2"><strong>Uploaded At:</strong> {format(parseISO(selectedDocumentDetails.uploaded_at), 'PPP p')}</li>
                      <li className="py-2"><strong>Last Modified:</strong> {format(parseISO(selectedDocumentDetails.last_modified_at), 'PPP p')}</li>
                      <li className="py-2">
                        <strong>Status:</strong> 
                        {selectedDocumentDetails.is_verified 
                          ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 ml-2">Verified</span> 
                          : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 ml-2">Not Verified</span>}
                      </li>
                      {selectedDocumentDetails.is_verified && (
                        <>
                          <li className="py-2"><strong>Verified By:</strong> {selectedDocumentDetails.verified_by_username}</li>
                          <li className="py-2"><strong>Verification Date:</strong> {format(parseISO(selectedDocumentDetails.verification_date), 'PPP p')}</li>
                        </>
                      )}
                      <li className="py-2">
                        <strong>File:</strong>{' '}
                        <a href={selectedDocumentDetails.file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900 ml-2 flex items-center">
                            <i className="bi bi-box-arrow-up-right mr-1"></i>View File
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
          )}
      
      {showPreviewModal && renderPreviewModal()}

      {showShareModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center animate__animated animate__fadeIn">
          <div className="relative p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center pb-3 border-b">
              <h5 className="text-xl font-bold">Share Document</h5>
              <button type="button" className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center" onClick={() => setShowShareModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-500 mb-4">Copy the link below to share the document.</p>
              <div className="flex items-center space-x-2">
                <input type="text" readOnly value={shareLink} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100" />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(shareLink);
                    showToast('Link copied to clipboard!');
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={() => setShowShareModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center animate__animated animate__fadeIn">
          <div className="relative p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center pb-3 border-b">
              <h5 className="text-xl font-bold">Confirm Deletion</h5>
              <button type="button" className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center" onClick={() => setShowDeleteModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-4">
              <p>Are you sure you want to delete this document? This action cannot be undone.</p>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button type="button" className="bg-red-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-red-700" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentManagement;
